// License-related filters

import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const licenseFilters: FilterRegistry = {
	licenseKeywords: {
		// No fulltextFields here on purpose — the shared filter builder would
		// only match against `licenseNumber` and `type`, but licenses also
		// need to match by client name (which lives across the address join).
		// licenses.service.ts handles `licenseKeywords` manually with the
		// proper join. Adding fulltextFields here would cause the shared
		// builder to apply a *narrower* clause that gets ANDed with the
		// service's clause, hiding licenses that only match by client name.
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search licenses...',
			objectTable: 'client_license',
			listKeys: ['license', 'client_licenses'],
		}),
		queryParamName: 'licenseKeywords',
	},

	licenseStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseStatus',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: [
			{ id: 'Active', label: 'Active' },
			{ id: 'Expired', label: 'Expired' },
			{ id: 'Pending', label: 'Pending' },
			{ id: 'Revoked', label: 'Revoked' },
			{ id: 'Suspended', label: 'Suspended' },
			{ id: 'Cancelled', label: 'Cancelled' },
			// 'Other' = the catch-all bucket on the dashboard chart:
			// every effective status that isn't one of the canonical
			// six above (e.g. registry-supplied 'Clear', 'Discontinued',
			// 'Revocation', or stray free-text). Surfaced as a filter
			// option so the chart segment is drillable. The custom handler
			// in typeormFilterBuilder.ts translates this to a NOT IN list.
			{ id: 'Other', label: 'Other' },
		],
		// 'Expired' is not stored on the row — it's derived from
		// expirationDate. The custom handler in typeormFilterBuilder.ts
		// translates each picked value to the right predicate so the
		// dropdown's five options stay coherent with reality.
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: ['license', 'client_licenses'],
	},

	// Restricts licenses by their owning client's lead/customer state.
	// The licenses list joins to client_address → client by default, so
	// the custom handler in typeormFilterBuilder.ts can filter on
	// `client.leadOrCustomer` directly. Used by the compliance dashboard
	// click-throughs to keep the count visible after drilling in
	// matching what the dashboard counted (customers only) — without it,
	// the list shows lead licenses too and the row count diverges from
	// the chart segment.
	licenseClientLeadOrCustomer: {
		type: 'radio',
		label: 'Client Type',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseClientLeadOrCustomer',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'Customer', label: 'Customers' },
			{ id: 'Lead', label: 'Leads' },
		],
		defaultValue: { id: 'All', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		listKeys: ['license', 'client_licenses'],
	},

	licenseType: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'Select type...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/type/options',
		queryParamName: 'licenseType',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		queryMapping: {
			objectTable: 'client_license',
			fieldName: 'type',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: ['license', 'client_licenses'],
	},

	licenseState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/state/options',
		queryParamName: 'licenseState',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		queryMapping: {
			objectTable: 'client_license',
			fieldName: 'issuingState',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: ['license', 'client_licenses'],
	},

	licenseClient: {
		...createEntityLookupFilter({
			label: 'Client',
			placeholder: 'Search clients...',
			apiEndpoint: '/clients',
			objectTable: 'client_license',
			fieldName: 'clientId',
			listKeys: ['license'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
			// `custom` (not `exists`): the actual SQL lives in the manual
			// `licenseClient` branch of LicensesService.findAll. The shared
			// `exists` branch in typeormFilterBuilder requires joinTable +
			// joinConditions metadata that we don't provide, so it no-ops
			// silently. Declaring `custom` makes the intent match reality and
			// prevents a future cleanup from removing the manual handler.
			joinType: 'custom',
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'licenseClient',
		valueExtractor: {
			property: 'id',
		},
	},

	licenseExpirationDateRange: {
		...createDateRangeFilter({
			label: 'Expiration Date',
			objectTable: 'client_license',
			dateField: 'expirationDate',
			listKeys: ['license', 'client_licenses'],
		}),
		queryParamName: 'licenseExpirationDateRange',
		allowClear: true,
	},

	licenseHasDisciplinary: {
		type: 'radio',
		label: 'Has Disciplinary Action',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseHasDisciplinary',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			fieldName: 'hasDisciplinaryAction',
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'client_licenses'],
	},

	licenseSalesRep: {
		type: 'multi-select',
		label: 'Sales Rep',
		placeholder: 'Select sales representatives...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/licenses/salesrep/options',
		options: [{ id: 'ME', label: 'Me' }],
		queryParamName: 'licenseSalesRep',
		multiple: true,
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// Filters licenses by match status: unmatched (no registry link) or pending review
	licenseMatchStatus: {
		type: 'radio',
		label: 'Match Status',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchStatus',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			// 'matched' is the inverse of 'unmatched' — it selects every
			// license linked to a registry record (fk_registry_license__id
			// IS NOT NULL). Distinct from 'confirmed', which requires a
			// client_license_match row of status 'confirmed'; most matched
			// licenses came in via initial harvest backfill and never had
			// a review record. The compliance-dashboard "Matched" KPI and
			// the "Matched" segment of the By-Match chart drill into this.
			{ id: 'matched', label: 'Matched' },
			{ id: 'unmatched', label: 'Unmatched' },
			{ id: 'under_review', label: 'Under Review' },
			{ id: 'pending_review', label: 'Pending Review' },
			{ id: 'has_pending_match', label: 'Pending Match' },
			{ id: 'confirmed', label: 'Confirmed' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// Coarse confidence-bucket filter for the License Matchmaker page.
	// Maps to the same client_license_match.confidence_score column as
	// licenseMatchConfidence, but encapsulates the thresholds so the UI
	// and the SQL agree. Boundaries are intentionally open-on-the-upper
	// side for medium/low (50<=x<85, x<50) so decimal scores (the column
	// is decimal(5,2)) don't fall through gaps between buckets.
	licenseMatchConfidenceLevel: {
		type: 'radio',
		label: 'Confidence',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchConfidenceLevel',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'high', label: 'High (≥85%)' },
			{ id: 'medium', label: 'Medium (50–84%)' },
			{ id: 'low', label: 'Low (<50%)' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// Filters licenses by the confidence score of their pending registry
	// match. Either bound may be omitted. The filter serializes to
	// `{ min, max }` on the wire (either can be null) via the same
	// property-extractor pattern used by date-range filters.
	licenseMatchConfidence: {
		type: 'number-range',
		label: 'Match Confidence (%)',
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchConfidence',
		multiple: false,
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		min: 0,
		max: 100,
		step: 1,
		suffix: '%',
		listKeys: ['license'],
	},

	// Filters licenses with recent registry changes (last 7 days)
	licenseRecentlyChanged: {
		type: 'radio',
		label: 'Recently Changed',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseRecentlyChanged',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// Filters licenses by whether any files are attached. Used for the
	// compliance "Missing Files" workflow where reviewers triage licenses
	// that lack supporting documentation.
	licenseHasFiles: {
		type: 'radio',
		label: 'Has Files',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseHasFiles',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'client_licenses'],
	},

	// Flags licenses whose stored values diverge from the matched registry
	// row for the user-visible fields (license number, expiration date).
	// Drives the "Discrepancies" tab and is also exposed as a standalone
	// filter so the user can flip a normal list view into discrepancy mode.
	// Comparison normalization is owned by the SQL handler in
	// typeormFilterBuilder (which mirrors `normalizeForDiscrepancy` in
	// libs/shared) — keep them in sync.
	licenseHasRegistryDiscrepancy: {
		type: 'radio',
		label: 'Registry Discrepancy',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseHasRegistryDiscrepancy',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Has Discrepancy' },
			{ id: 'no', label: 'Matches Registry' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},
};
